/* Editor — Review Notes Inbox (2026-10).
 *
 * Every AI evaluate/rewrite note across every manuscript, newest first —
 * no need to open each manuscript individually to see what Batch AI
 * Polish (or a one-off ai-assist run) produced.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ChevronDown, ChevronUp, BookOpen, Sparkles } from 'lucide-react';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const MODE_LABEL = { evaluate: 'Evaluate', rewrite: 'Rewrite', question: 'Question' };

function timeAgo(iso) {
  if (!iso) return '';
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function EditorAiNotesInboxPanel() {
  const [notes,   setNotes]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get('/editor/ai-notes', { params: { limit: 50 } });
      setNotes(r.data?.notes || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load AI notes.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <section data-testid="editor-ai-notes-inbox-panel" className="space-y-3">
      <div>
        <div className="text-[13px] font-semibold">Review Notes Inbox</div>
        <div className="text-[10.5px] text-muted-foreground">{total} AI evaluation / rewrite notes across all manuscripts — newest first.</div>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading notes…
        </div>
      ) : notes.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground" data-testid="editor-ai-notes-empty">
          No AI notes yet — run AI Assist or Batch AI Polish from All Manuscripts to generate some.
        </div>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => {
            const isOpen = expanded.has(n.id);
            return (
              <div key={n.id} data-testid={`editor-ai-note-${n.id}`}
                   className="rounded-md border border-border">
                <button
                  type="button"
                  onClick={() => toggle(n.id)}
                  data-testid={`editor-ai-note-toggle-${n.id}`}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-secondary/40"
                >
                  <span className="inline-flex min-w-0 items-center gap-2 text-[12px]">
                    <BookOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate font-semibold">{n.manuscript_title || 'Untitled'}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-medium text-primary">
                      <Sparkles className="h-3 w-3" /> {MODE_LABEL[n.mode] || n.mode}
                    </span>
                    <span className="hidden shrink-0 text-[10.5px] text-muted-foreground sm:inline">{n.model}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-[10.5px] text-muted-foreground">
                    {timeAgo(n.created_at)}
                    {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-border px-3 py-2 text-[12px] leading-relaxed" data-testid={`editor-ai-note-body-${n.id}`}>
                    <pre className="whitespace-pre-wrap font-sans">{n.output_text}</pre>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
